import { z } from "zod";

import {
  ACHIEVEMENT_IDS,
  CONTRACT_TYPES,
  NODE_TYPES,
  RECOMMENDED_ACTION_CODES,
  RISK_LEVELS,
  SEVERITIES,
  SKILL_IDS,
  TOOL_IDS,
} from "../types/enums";

const severitySchema = z.enum(SEVERITIES);
const riskLevelSchema = z.enum(RISK_LEVELS);
const nodeTypeSchema = z.enum(NODE_TYPES);
const contractTypeSchema = z.enum(CONTRACT_TYPES);
const toolIdSchema = z.enum(TOOL_IDS);
const skillIdSchema = z.enum(SKILL_IDS);
const achievementIdSchema = z.enum(ACHIEVEMENT_IDS);
const recommendedActionCodeSchema = z.enum(RECOMMENDED_ACTION_CODES);

const nonEmptyString = z.string().min(1);
const nonNegativeInt = z.number().int().min(0);
const localizedText = z
  .object({ ru: z.string().min(1), en: z.string().min(1) })
  .strict();

export const logEventSchema = z
  .object({
    timestamp: nonEmptyString,
    actor: nonEmptyString,
    action: localizedText,
    target: nonEmptyString,
    severity: severitySchema,
    isSuspicious: z.boolean(),
    explanation: localizedText,
  })
  .strict();

const tokenString = z.string().min(1).regex(/^\S+$/, "must not contain whitespace");

export const phishingEmailSchema = z
  .object({
    receivedAt: nonEmptyString,
    from: z.object({ name: localizedText, address: tokenString }).strict(),
    subject: localizedText,
    preview: localizedText,
    link: z.object({ text: localizedText, href: tokenString }).strict().optional(),
    attachment: nonEmptyString.optional(),
    isPhishing: z.boolean(),
    explanation: localizedText,
  })
  .strict();

export const phishingTaskSchema = z
  .object({
    trustedDomains: z.array(tokenString).min(1),
    emails: z.array(phishingEmailSchema).min(4).max(10),
  })
  .strict()
  .superRefine((task, ctx) => {
    const phishing = task.emails.filter((email) => email.isPhishing).length;
    if (phishing === 0 || phishing === task.emails.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["emails"],
        message: "phishing task needs at least one phishing and one legit email",
      });
    }
  });

export const networkNodeSchema = z
  .object({
    id: nonEmptyString,
    type: nodeTypeSchema,
    riskLevel: riskLevelSchema,
    connections: z.array(nonEmptyString),
    isRisky: z.boolean(),
    recommendedMeasure: recommendedActionCodeSchema.nullable(),
    riskRationale: localizedText,
  })
  .strict()
  .superRefine((node, ctx) => {
    if (node.isRisky && node.recommendedMeasure === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recommendedMeasure"],
        message: "risky node must declare a recommendedMeasure",
      });
    }
  });

export const findingSchema = z
  .object({
    id: nonEmptyString,
    title: localizedText,
    severity: severitySchema,
    recommendedActionCode: recommendedActionCodeSchema,
    cost: nonNegativeInt,
  })
  .strict();

export const rewardSchema = z
  .object({
    money: z.number().int(),
    xp: z.number().int(),
    reputation: z.number().int(),
  })
  .strict();

export const contractSchema = z
  .object({
    id: nonEmptyString,
    clientId: nonEmptyString,
    title: localizedText,
    brief: localizedText,
    description: localizedText.optional(),
    difficulty: z.number().int().min(1).max(3),
    riskLevel: riskLevelSchema.optional(),
    requiredReputation: nonNegativeInt,
    tasks: z.array(contractTypeSchema).min(1).max(3),
    primaryTask: contractTypeSchema,
    recommendedSkills: z.array(skillIdSchema).optional(),
    reward: rewardSchema.optional(),
    logEvents: z.array(logEventSchema).optional(),
    networkNodes: z.array(networkNodeSchema).optional(),
    phishing: phishingTaskSchema.optional(),
    findings: z.array(findingSchema).optional(),
    clientBudget: z.number().int().positive().optional(),
  })
  .strict()
  .superRefine((contract, ctx) => {
    if (!contract.tasks.includes(contract.primaryTask)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["primaryTask"],
        message: `primaryTask "${contract.primaryTask}" is not present in tasks`,
      });
    }
    if (contract.tasks.includes("log_analysis") && !contract.logEvents) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["logEvents"],
        message: "contract has log_analysis task but no logEvents",
      });
    }
    if (contract.tasks.includes("network_map") && !contract.networkNodes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["networkNodes"],
        message: "contract has network_map task but no networkNodes",
      });
    }
    if (contract.tasks.includes("phishing") && !contract.phishing) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phishing"],
        message: "contract has phishing task but no phishing block",
      });
    }
    if (contract.tasks.includes("report_builder")) {
      if (!contract.findings) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["findings"],
          message: "contract has report_builder task but no findings",
        });
      }
      if (contract.clientBudget === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["clientBudget"],
          message: "contract has report_builder task but no clientBudget",
        });
      }
    }
  });

export const clientSchema = z
  .object({
    id: nonEmptyString,
    name: localizedText,
    trust: z.number().int().min(0).max(100).optional(),
    requiredReputation: nonNegativeInt.optional(),
    unlockContractIds: z.array(nonEmptyString).optional(),
  })
  .strict();

export const toolSchema = z
  .object({
    id: toolIdSchema,
    name: localizedText,
    description: localizedText,
    effect: localizedText,
    category: z.enum(["log_analysis", "network_map", "phishing", "report_builder", "upcoming"]),
    status: z.enum(["active", "upcoming"]),
    upcomingType: localizedText.optional(),
    cost: z.number().int().min(0),
    minLevel: z.number().int().min(1).max(10).optional(),
    requiredReputation: nonNegativeInt.optional(),
  })
  .strict();

export const skillSchema = z
  .object({
    id: skillIdSchema,
    name: localizedText,
    description: localizedText,
    effects: z.array(localizedText),
    category: z.enum(["log_analysis", "network_map", "phishing", "report_builder", "economy", "upcoming"]),
    status: z.enum(["active", "upcoming"]),
    upcomingType: localizedText.optional(),
    minLevel: z.number().int().min(1).max(10).optional(),
  })
  .strict();

export const achievementSchema = z
  .object({
    id: achievementIdSchema,
    name: localizedText,
    description: localizedText,
  })
  .strict();

export const recommendationSchema = z
  .object({
    code: recommendedActionCodeSchema,
    text: nonEmptyString,
  })
  .strict();

export const contentFileSchema = z
  .object({
    clients: z.array(clientSchema).optional(),
    contracts: z.array(contractSchema).optional(),
    tools: z.array(toolSchema).optional(),
    skills: z.array(skillSchema).optional(),
    achievements: z.array(achievementSchema).optional(),
    recommendations: z.array(recommendationSchema).optional(),
  })
  .strict();

export type ContentFile = z.infer<typeof contentFileSchema>;
